import enhancedSecureTokenManager from '../security/EnhancedSecureTokenManager.js';
import unifiedGoogleTokenManager from '../services/UnifiedGoogleTokenManager.js';
import IndexedDBCache from '../cache/IndexedDBCache.js';

/**
 * Enhanced Google Contacts API client with advanced features:
 * - Automatic token refresh and retry logic
 * - Rate limiting and exponential backoff
 * - Advanced caching with IndexedDB
 * - Performance monitoring and analytics
 * - Privacy-conscious temporary storage
 */
class EnhancedGoogleContactsAPI {
  constructor(options = {}) {
    this.baseURL = 'https://people.googleapis.com/v1';
    this.cache = null;
    this.requestQueue = [];
    this.isInitialized = false;
    
    // Rate limiting configuration
    this.rateLimit = {
      requests: 0,
      windowStart: Date.now(),
      maxRequests: options.maxRequests || 100,
      windowMs: options.windowMs || 60000, // 1 minute
      retryAfter: options.retryAfter || 1000
    };
    
    // Retry configuration with exponential backoff
    this.retryConfig = {
      maxRetries: options.maxRetries || 3,
      baseDelay: options.baseDelay || 1000,
      maxDelay: options.maxDelay || 30000,
      backoffFactor: options.backoffFactor || 2
    };
    
    // Enhanced token manager
    this.tokenManager = enhancedSecureTokenManager;
    this.currentUserId = null;
    
    // Analytics
    this.analytics = {
      requests: 0,
      errors: 0,
      retries: 0,
      cacheHits: 0,
      cacheMisses: 0,
      averageResponseTime: 0,
      totalResponseTime: 0
    };
    
    // Initialize
    this.init();
  }

  /**
   * Initialize the API client
   */
  async init() {
    try {
      this.cache = new IndexedDBCache({
        dbName: 'ContactsAPICache',
        defaultTTL: 5 * 60 * 1000 // 5 minutes for privacy
      });
      
      await this.cache.init();
      this.isInitialized = true;
      
      // Setup enhanced token refresh monitoring
      this.setupEnhancedTokenRefreshMonitoring();
      
      console.log('Enhanced Google Contacts API client initialized');
    } catch (error) {
      console.error('Failed to initialize Enhanced Google Contacts API:', error);
      throw error;
    }
  }

  /**
   * Setup enhanced token refresh monitoring
   */
  setupEnhancedTokenRefreshMonitoring() {
    // Listen for token refresh events from the enhanced manager
    this.tokenManager.onTokenRefresh((type, userId, data) => {
      if (type === 'success') {
        console.log('✅ Token refresh successful for user:', userId);
        
        // Notify UI about successful refresh
        window.dispatchEvent(new CustomEvent('contacts-token-refreshed', {
          detail: {
            userId,
            refreshedAt: new Date().toISOString(),
            automatic: true
          }
        }));
      } else if (type === 'error') {
        console.error('❌ Token refresh failed for user:', userId, data);
        this.handleAuthenticationError(userId, data);
      }
    });
  }

  /**
   * Initialize with user context
   */
  async initializeWithUser() {
    // Get current user from Firebase Auth
    const auth = await import('firebase/auth');
    const { getAuth } = auth;
    const firebaseAuth = getAuth();
    
    if (!firebaseAuth.currentUser) {
      throw new Error('No authenticated user');
    }

    this.currentUserId = firebaseAuth.currentUser.uid;
    
    // Migrate existing tokens from localStorage if needed
    await this.migrateExistingTokens();
    
    console.log(`🔧 Contacts API initialized for user: ${this.currentUserId}`);
    return this.currentUserId;
  }

  /**
   * Migrate existing tokens from localStorage to enhanced secure storage
   */
  async migrateExistingTokens() {
    try {
      const cacheKey = 'handbook_token_cache';
      const cache = JSON.parse(localStorage.getItem(cacheKey) || '{}');
      const userTokenData = cache[this.currentUserId];
      
      if (userTokenData && userTokenData.accessToken) {
        console.log('🔄 Migrating existing token to enhanced storage...');
        
        // Check if already in enhanced storage
        const existingToken = await this.tokenManager.getToken(this.currentUserId);
        if (!existingToken) {
          // Migrate to enhanced storage
          const tokenData = {
            accessToken: userTokenData.accessToken,
            refreshToken: userTokenData.refreshToken,
            clientId: userTokenData.clientId || process.env.REACT_APP_GOOGLE_CLIENT_ID,
            scope: 'contacts.readonly'
          };
          
          await this.tokenManager.storeToken(
            this.currentUserId, 
            tokenData, 
            userTokenData.expiresIn || 3600
          );
          
          console.log('✅ Token migration completed');
        }
      }
    } catch (error) {
      console.warn('Token migration failed:', error);
    }
  }

  /**
   * Ensure API is initialized
   */
  async ensureInitialized() {
    if (!this.isInitialized) {
      await new Promise(resolve => {
        const checkInit = () => {
          if (this.isInitialized) {
            resolve();
          } else {
            setTimeout(checkInit, 100);
          }
        };
        checkInit();
      });
    }
  }

  /**
   * Check rate limiting
   */
  async checkRateLimit() {
    const now = Date.now();
    
    // Reset window if needed
    if (now - this.rateLimit.windowStart >= this.rateLimit.windowMs) {
      this.rateLimit.requests = 0;
      this.rateLimit.windowStart = now;
    }
    
    // Check if we've exceeded the limit
    if (this.rateLimit.requests >= this.rateLimit.maxRequests) {
      const waitTime = this.rateLimit.windowMs - (now - this.rateLimit.windowStart);
      if (waitTime > 0) {
        console.log(`Rate limit exceeded, waiting ${waitTime}ms`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        return this.checkRateLimit();
      }
    }
    
    this.rateLimit.requests++;
  }

  /**
   * Get valid access token with enhanced management
   */
  async getValidAccessToken() {
    try {
      // Ensure we have user context
      if (!this.currentUserId) {
        await this.initializeWithUser();
      }

      // First, try to get token from enhanced secure storage
      const tokenInfo = await this.tokenManager.getToken(this.currentUserId);
      
      if (tokenInfo && tokenInfo.accessToken) {
        console.log('✅ Using token from enhanced secure storage');
        return tokenInfo.accessToken;
      }

      console.log('⚠️ No valid token in enhanced storage, checking unified token manager...');
      
      // Try to get token from unified token manager (new system)
      try {
        const unifiedToken = await unifiedGoogleTokenManager.getToken(this.currentUserId, 'contacts');
        if (unifiedToken) {
          console.log('✅ Using token from unified token manager');
          return unifiedToken;
        }
      } catch (unifiedError) {
        console.log('⚠️ Unified token manager check failed:', unifiedError.message);
        // Continue to try other methods
      }
      
      // Try migration from old localStorage cache
      await this.migrateExistingTokens();
      
      // Try again after migration
      const migratedToken = await this.tokenManager.getToken(this.currentUserId);
      if (migratedToken && migratedToken.accessToken) {
        console.log('✅ Using migrated token');
        return migratedToken.accessToken;
      }
      
      // Final fallback: try unified token manager with combined scope
      try {
        const fallbackToken = await unifiedGoogleTokenManager.getToken(this.currentUserId, 'combined');
        if (fallbackToken) {
          console.log('✅ Using token from unified token manager (combined scope)');
          return fallbackToken;
        }
      } catch (fallbackError) {
        console.log('⚠️ Unified token manager fallback failed:', fallbackError.message);
      }
      
      throw new Error('No valid Google OAuth access token available. Please authenticate with Google to access Contacts.');
    } catch (error) {
      console.error('Failed to get access token:', error);
      throw error;
    }
  }


  /**
   * Prepare request options
   */
  async prepareRequest(accessToken, options = {}) {
    const headers = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers
    };

    return {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      ...options
    };
  }

  /**
   * Make request with enhanced retry logic and token refresh
   */
  async makeRequestWithRetry(url, options, retryCount = 0) {
    try {
      const response = await fetch(url, options);
      
      // Handle authentication errors with token refresh
      if (response.status === 401 && retryCount < this.retryConfig.maxRetries) {
        console.log('🔄 401 Unauthorized - attempting token refresh...');
        
        // Trigger token refresh through enhanced manager
        if (this.currentUserId) {
          try {
            await this.tokenManager.requestTokenRefresh(this.currentUserId);
            
            // Get fresh token and retry request
            const freshToken = await this.getValidAccessToken();
            const newOptions = {
              ...options,
              headers: {
                ...options.headers,
                'Authorization': `Bearer ${freshToken}`
              }
            };
            
            console.log('🔄 Retrying request with refreshed token...');
            return this.makeRequestWithRetry(url, newOptions, retryCount + 1);
          } catch (refreshError) {
            console.error('Token refresh failed during request retry:', refreshError);
            this.handleAuthenticationError(this.currentUserId, refreshError);
            throw refreshError;
          }
        }
      }
      
      // Handle rate limiting with enhanced backoff
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('Retry-After') || '1') * 1000;
        const enhancedDelay = Math.max(retryAfter, this.retryConfig.baseDelay * Math.pow(2, retryCount));
        
        console.log(`⏳ Rate limited - waiting ${enhancedDelay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, enhancedDelay));
        return this.makeRequestWithRetry(url, options, retryCount);
      }
      
      // Handle server errors with exponential backoff
      if (response.status >= 500 && retryCount < this.retryConfig.maxRetries) {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffFactor, retryCount),
          this.retryConfig.maxDelay
        );
        
        console.log(`⏳ Server error ${response.status} - retrying in ${delay}ms...`);
        this.analytics.retries++;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequestWithRetry(url, options, retryCount + 1);
      }
      
      return response;
    } catch (error) {
      // Network errors and other fetch failures
      if (retryCount < this.retryConfig.maxRetries) {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffFactor, retryCount),
          this.retryConfig.maxDelay
        );
        
        console.log(`⏳ Network error - retrying in ${delay}ms...`);
        this.analytics.retries++;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequestWithRetry(url, options, retryCount + 1);
      }
      
      throw error;
    }
  }

  /**
   * Handle response
   */
  async handleResponse(response, url, options) {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorDetails = null;
      
      // Try to get detailed error information from response body
      try {
        const errorResponse = await response.text();
        if (errorResponse) {
          console.log(`API Error Response for ${url}:`, errorResponse);
          try {
            errorDetails = JSON.parse(errorResponse);
            if (errorDetails.error) {
              if (errorDetails.error.message) {
                errorMessage = `HTTP ${response.status}: ${errorDetails.error.message}`;
              }
              console.log('Detailed error info:', errorDetails.error);
            }
          } catch (parseError) {
            // If it's not JSON, just log the raw response
            console.log('Raw error response (not JSON):', errorResponse);
            errorMessage = `HTTP ${response.status}: ${errorResponse}`;
          }
        }
      } catch (readError) {
        console.warn('Could not read error response:', readError);
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = response;
      error.details = errorDetails;
      
      if (response.status === 401) {
        this.handleAuthenticationError();
      }
      
      throw error;
    }

    const data = await response.json();
    
    // Cache GET responses
    if ((!options.method || options.method === 'GET') && this.cache) {
      try {
        await this.cache.cacheAPIResponse(url, 'GET', data, response.headers.get('ETag'));
      } catch (cacheError) {
        console.warn('Cache write error:', cacheError);
      }
    }

    return data;
  }

  /**
   * Update analytics
   */
  updateAnalytics(responseTime) {
    this.analytics.totalResponseTime += responseTime;
    this.analytics.averageResponseTime = this.analytics.totalResponseTime / this.analytics.requests;
  }

  /**
   * Handle authentication errors with enhanced cleanup
   */
  handleAuthenticationError(userId = null, error = null) {
    const targetUserId = userId || this.currentUserId;
    console.warn(`Authentication error in Contacts API for user: ${targetUserId}`);
    
    // Clear token cache from enhanced storage
    if (targetUserId) {
      this.tokenManager.removeToken(targetUserId);
    }
    
    // Clear legacy localStorage cache for migration compatibility
    const cacheKey = 'handbook_token_cache';
    if (targetUserId) {
      try {
        const cache = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        delete cache[targetUserId];
        localStorage.setItem(cacheKey, JSON.stringify(cache));
      } catch (e) {
        console.warn('Failed to clean localStorage cache:', e);
      }
    }
    
    // Notify user to re-authenticate with enhanced detail
    window.dispatchEvent(new CustomEvent('contacts-auth-required', {
      detail: {
        reason: error?.message || 'Token expired or invalid',
        action: 'enhanced_refresh_failed',
        userId: targetUserId,
        timestamp: new Date().toISOString()
      }
    }));
  }

  /**
   * Ensure valid token with enhanced proactive management
   */
  async ensureValidToken() {
    try {
      // Ensure we have user context
      if (!this.currentUserId) {
        await this.initializeWithUser();
      }
      
      // First, try to get token directly (this will check all token managers)
      try {
        return await this.getValidAccessToken();
      } catch (getTokenError) {
        // If getValidAccessToken fails, check token health for more details
        console.log('⚠️ getValidAccessToken failed, checking token health...');
      }
      
      // Check token health with enhanced manager
      let health;
      try {
        health = await this.tokenManager.getTokenHealth(this.currentUserId);
      } catch (healthError) {
        // Enhanced manager has no token, check unified manager
        console.log('⚠️ Enhanced manager has no token, checking unified token manager...');
        try {
          const unifiedHealth = await unifiedGoogleTokenManager.getTokenHealth(this.currentUserId, 'contacts');
          if (unifiedHealth.status === 'valid') {
            // Unified manager has a valid token, get it
            const unifiedToken = await unifiedGoogleTokenManager.getToken(this.currentUserId, 'contacts');
            if (unifiedToken) {
              console.log('✅ Found token in unified token manager');
              return unifiedToken;
            }
          }
          // Unified manager also doesn't have token, throw error
          throw new Error('No valid Google OAuth access token available. Please authenticate with Google to access Contacts.');
        } catch (unifiedError) {
          // Neither manager has a token
          throw new Error('No valid Google OAuth access token available. Please authenticate with Google to access Contacts.');
        }
      }
      
      if (health.status === 'valid' && !health.needsRefresh) {
        // Token is healthy, get it
        return await this.getValidAccessToken();
      }
      
      if (health.status === 'valid' && health.needsRefresh) {
        console.log('🔄 Token needs proactive refresh...');
        // Token exists but needs refresh - the enhanced manager will handle this automatically
        // Just try to get the token, which will trigger refresh if needed
        return await this.getValidAccessToken();
      }
      
      if (health.status === 'missing') {
        // Enhanced manager has no token, check unified manager before throwing error
        console.log('🚫 Enhanced manager has no token, checking unified token manager...');
        try {
          const unifiedToken = await unifiedGoogleTokenManager.getToken(this.currentUserId, 'contacts');
          if (unifiedToken) {
            console.log('✅ Found token in unified token manager');
            return unifiedToken;
          }
          // Try combined scope as fallback
          const fallbackToken = await unifiedGoogleTokenManager.getToken(this.currentUserId, 'combined');
          if (fallbackToken) {
            console.log('✅ Found token in unified token manager (combined scope)');
            return fallbackToken;
          }
        } catch (unifiedError) {
          console.log('⚠️ Unified token manager also has no token:', unifiedError.message);
        }
        
        // No token found in either manager
        throw new Error('No valid Google OAuth access token available. Please authenticate with Google to access Contacts.');
      }
      
      // Fallback - try to get token anyway
      return await this.getValidAccessToken();
    } catch (error) {
      console.warn('Enhanced token management failed:', error);
      throw error;
    }
  }

  /**
   * Make authenticated request with all enhancements
   */
  async makeRequest(endpoint, options = {}) {
    await this.ensureInitialized();
    
    const startTime = Date.now();
    this.analytics.requests++;
    
    try {
      // Check rate limiting
      await this.checkRateLimit();
      
      // Build full URL
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
      
      // Check cache first (for GET requests)
      if (options.method === 'GET' || !options.method) {
        try {
          const cached = await this.cache.getCachedAPIResponse(url, 'GET');
          if (cached) {
            this.analytics.cacheHits++;
            return cached.response;
          }
        } catch (cacheError) {
          console.warn('Cache read error:', cacheError);
        }
        this.analytics.cacheMisses++;
      }
      
      // Get access token with proactive refresh
      const accessToken = await this.ensureValidToken();
      
      // Prepare request
      const requestOptions = await this.prepareRequest(accessToken, options);
      
      // Make request with retry logic
      const response = await this.makeRequestWithRetry(url, requestOptions);
      
      // Handle response
      const result = await this.handleResponse(response, url, options);
      
      // Update analytics
      const responseTime = Date.now() - startTime;
      this.updateAnalytics(responseTime);
      
      return result;
      
    } catch (error) {
      this.analytics.errors++;
      const responseTime = Date.now() - startTime;
      this.updateAnalytics(responseTime);
      
      throw this.enhanceError(error, endpoint, options);
    }
  }

  /**
   * Enhance error with context
   */
  enhanceError(error, endpoint, options) {
    const enhancedError = new Error(error.message);
    enhancedError.originalError = error;
    enhancedError.endpoint = endpoint;
    enhancedError.options = options;
    enhancedError.timestamp = new Date().toISOString();
    
    return enhancedError;
  }

  /**
   * Get contacts list
   */
  async getContacts(options = {}) {
    const params = new URLSearchParams();
    
    // Default fields for contacts
    const personFields = options.personFields || 
      'names,emailAddresses,phoneNumbers,photos,organizations';
    
    params.set('personFields', personFields);
    
    // Use maximum page size to get more contacts
    if (options.pageSize) {
      params.set('pageSize', Math.min(options.pageSize, 1000));
    } else {
      params.set('pageSize', 1000); // Maximum page size
    }
    
    if (options.pageToken) {
      params.set('pageToken', options.pageToken);
    }
    
    // Add sortOrder for better organization
    params.set('sortOrder', 'LAST_MODIFIED_DESCENDING');

    const endpoint = `/people/me/connections?${params.toString()}`;
    return await this.makeRequest(endpoint, { method: 'GET' });
  }

  /**
   * Get ALL contacts (with pagination)
   */
  async getAllContacts(options = {}) {
    let allContacts = [];
    let nextPageToken = null;
    let pageCount = 0;
    const maxPages = 10; // Safety limit
    
    console.log('Starting to fetch all contacts...');
    
    do {
      try {
        const response = await this.getContacts({
          ...options,
          pageToken: nextPageToken,
          pageSize: 1000
        });
        
        if (response.connections) {
          allContacts = allContacts.concat(response.connections);
          console.log(`Page ${pageCount + 1}: ${response.connections.length} contacts (Total: ${allContacts.length})`);
        }
        
        nextPageToken = response.nextPageToken;
        pageCount++;
        
      } catch (error) {
        console.error(`Error fetching contacts page ${pageCount + 1}:`, error);
        break;
      }
    } while (nextPageToken && pageCount < maxPages);
    
    console.log(`Finished fetching contacts. Total: ${allContacts.length}`);
    
    return {
      connections: allContacts,
      totalCount: allContacts.length
    };
  }

  /**
   * Search contacts using Google's search API
   */
  async searchContacts(query, options = {}) {
    if (!query) {
      return { results: [] };
    }

    // Debug: Check current token scopes
    try {
      const token = await this.getValidAccessToken();
      console.log('Current token info - checking scopes...');
      await this.checkTokenScopes(token);
    } catch (error) {
      console.warn('Could not check token scopes:', error);
    }

    // For phone numbers, try direct connections endpoint first since searchContacts API
    // doesn't work well with phone numbers
    const isPhoneNumber = query.match(/^[+\-()\s\d]+$/);
    if (isPhoneNumber) {
      console.log('Phone number detected, using fallback method directly');
      const cleanedQuery = this.cleanSearchQuery(query);
      return await this.searchContactsFallback(cleanedQuery, options);
    }

    // Clean the query for better search results
    const cleanedQuery = this.cleanSearchQuery(query);
    
    const params = new URLSearchParams();
    const readMask = options.readMask || 
      'names,emailAddresses,phoneNumbers,photos,organizations';
    
    params.set('readMask', readMask);
    params.set('query', cleanedQuery);
    params.set('pageSize', options.pageSize || 25);

    const endpoint = `/people:searchContacts?${params.toString()}`;
    console.log('Attempting searchContacts with endpoint:', endpoint);
    console.log('Full URL would be:', `${this.baseURL}${endpoint}`);
    
    try {
      console.log('✅ Using efficient direct search (only matching contacts returned)');
      const response = await this.makeRequest(endpoint, { method: 'GET' });
      const resultCount = response.results?.length || 0;
      console.log(`✅ Direct search found ${resultCount} matching contacts`);
      
      // If direct search returns empty results, try fallback method as backup
      // Google's search API can miss contacts sometimes
      if (resultCount === 0) {
        console.log('🔄 Direct search returned 0 results, trying fallback method as backup...');
        const fallbackResults = await this.searchContactsFallback(cleanedQuery, options);
        if (fallbackResults.results?.length > 0) {
          console.log(`💡 Fallback found ${fallbackResults.results.length} contacts that direct search missed!`);
          return fallbackResults;
        } else {
          console.log('📭 Fallback also found 0 results - no matches exist');
        }
      }
      
      return response;
    } catch (error) {
      // If search fails, try getting connections and filtering
      console.warn('❌ Direct search failed, trying fallback method:', error.message);
      console.warn('⚠️  This is less efficient - downloads multiple contacts and filters locally');
      return await this.searchContactsFallback(cleanedQuery, options);
    }
  }

  /**
   * Clean search query to work better with Google APIs
   */
  cleanSearchQuery(query) {
    // Remove special characters that might cause issues
    let cleaned = query.trim();
    
    // For phone numbers, remove + and other formatting characters
    if (cleaned.match(/^[+\-()\s\d]+$/)) {
      // This looks like a phone number - remove all formatting
      cleaned = cleaned.replace(/[+\-()\s]/g, '');
      console.log(`Cleaned phone number: "${query}" -> "${cleaned}"`);
    } else {
      // For other queries, just clean up extra whitespace and problematic chars
      cleaned = cleaned.replace(/[^\w\s@.-]/g, '').replace(/\s+/g, ' ');
      console.log(`Cleaned search query: "${query}" -> "${cleaned}"`);
    }
    
    return cleaned;
  }

  /**
   * Fallback search method using connections endpoint
   */
  async searchContactsFallback(query, options = {}) {
    try {
      console.log('⚠️  Using fallback search method (less efficient)...');
      console.log('💡 Fallback downloads multiple contacts and filters client-side');
      
      // Use smaller page size to reduce unnecessary data transfer
      const response = await this.getContacts({
        pageSize: Math.min(options.pageSize || 100, 200), // Reduced from 1000
        ...options
      });
      
      console.log(`Fallback got ${response.connections?.length || 0} total contacts to search through`);
      
      if (!response.connections) {
        console.log('No connections found in fallback');
        return { results: [] };
      }

      // Debug: Log the first few contacts to see what data we have
      console.log('Sample contacts found:', response.connections.slice(0, 3).map(contact => ({
        names: contact.names?.map(name => ({
          displayName: name.displayName,
          givenName: name.givenName,
          familyName: name.familyName,
          fullName: `${name.givenName || ''} ${name.familyName || ''}`.trim()
        })),
        emails: contact.emailAddresses?.map(email => email.value),
        phones: contact.phoneNumbers?.map(phone => phone.value),
        organizations: contact.organizations?.map(org => org.name)
      })));
      
      console.log(`🔍 Searching for query: "${query}" (lowercase: "${query.toLowerCase()}")`);
      console.log(`🔍 Clean phone search: "${query.replace(/[+\-()\s]/g, '')}"`);
      
      // Test the first contact against our search
      if (response.connections.length > 0) {
        const testContact = response.connections[0];
        console.log('🧪 Testing first contact against search logic:');
        console.log('📇 Contact details:', {
          names: testContact.names,
          emails: testContact.emailAddresses,
          phones: testContact.phoneNumbers,
          organizations: testContact.organizations
        });
      }

      // Filter results client-side
      const queryLower = query.toLowerCase();
      const cleanSearchPhone = query.replace(/[+\-()\s]/g, ''); // For phone number matching
      
      const filtered = response.connections.filter(contact => {
        // Search in names
        if (contact.names) {
          for (const name of contact.names) {
            if (name.displayName?.toLowerCase().includes(queryLower) ||
                name.givenName?.toLowerCase().includes(queryLower) ||
                name.familyName?.toLowerCase().includes(queryLower)) {
              console.log(`✅ Found match in name: ${name.displayName || name.givenName + ' ' + name.familyName}`);
              return true;
            }
          }
        }
        
        // Search in emails
        if (contact.emailAddresses) {
          for (const email of contact.emailAddresses) {
            if (email.value?.toLowerCase().includes(queryLower)) {
              console.log(`✅ Found match in email: ${email.value}`);
              return true;
            }
          }
        }
        
        // Search in phone numbers (both formatted and clean)
        if (contact.phoneNumbers) {
          for (const phone of contact.phoneNumbers) {
            const phoneValue = phone.value || '';
            const cleanPhone = phoneValue.replace(/[+\-()\s]/g, '');
            
            if (phoneValue.toLowerCase().includes(queryLower) ||
                cleanPhone.includes(cleanSearchPhone) ||
                phoneValue.includes(query)) {
              console.log(`✅ Found match in phone: ${phoneValue}`);
              return true;
            }
          }
        }
        
        // Search in organizations
        if (contact.organizations) {
          for (const org of contact.organizations) {
            if (org.name?.toLowerCase().includes(queryLower)) {
              console.log(`✅ Found match in organization: ${org.name}`);
              return true;
            }
          }
        }
        
        return false;
      });

      console.log(`Fallback filtered down to ${filtered.length} matching contacts`);
      return { results: filtered.map(contact => ({ person: contact })) };
    } catch (error) {
      console.error('Fallback search also failed:', error);
      return { results: [] };
    }
  }


  /**
   * Get contact by resource name
   */
  async getContact(resourceName, options = {}) {
    const params = new URLSearchParams();
    const personFields = options.personFields || 
      'names,emailAddresses,phoneNumbers,photos,organizations';
    
    params.set('personFields', personFields);

    const endpoint = `/${resourceName}?${params.toString()}`;
    return await this.makeRequest(endpoint, { method: 'GET' });
  }


  /**
   * Try alternative directory contacts access
   */
  async searchDirectoryContactsAlternative(query, options = {}) {
    try {
      console.log('Trying alternative directory contacts access...');
      
      // Try otherContacts endpoint which might include directory contacts
      const params = new URLSearchParams();
      params.set('pageSize', Math.min(options.pageSize || 50, 100)); // Reduced from potentially unlimited
      params.set('readMask', 'names,emailAddresses,phoneNumbers,photos,organizations,sources');
      
      const endpoint = `/otherContacts?${params.toString()}`;
      console.log('Alternative directory endpoint:', endpoint);
      console.log('📋 IMPORTANT: This endpoint requires https://www.googleapis.com/auth/contacts.other.readonly scope');
      console.log('📋 Current token only has contacts.readonly scope - this will likely cause a 403 error');
      
      const response = await this.makeRequest(endpoint, { method: 'GET' });
      
      if (response.otherContacts) {
        // Filter for directory/domain contacts
        const directoryContacts = response.otherContacts
          .filter(contact => {
            // Look for contacts that might be from organization
            return contact.sources?.some(source => 
              source.type === 'DOMAIN_CONTACT' || 
              source.type === 'DIRECTORY' ||
              contact.emailAddresses?.some(email => 
                email.value?.includes('@') && 
                email.value.toLowerCase().includes(query.toLowerCase())
              )
            );
          })
          .filter(contact => {
            // Apply search filter
            const queryLower = query.toLowerCase();
            return contact.names?.some(name => 
              name.displayName?.toLowerCase().includes(queryLower)
            ) || contact.emailAddresses?.some(email =>
              email.value?.toLowerCase().includes(queryLower)
            );
          })
          .map(contact => ({
            id: contact.resourceName,
            resourceName: contact.resourceName,
            name: contact.names?.[0]?.displayName || 'Unnamed Contact',
            email: contact.emailAddresses?.[0]?.value || null,
            phone: contact.phoneNumbers?.[0]?.value || null,
            photo: contact.photos?.[0]?.url || null,
            organization: contact.organizations?.[0]?.name || null,
            isDirectory: true,
            rawContact: contact
          }));
        
        console.log(`Found ${directoryContacts.length} alternative directory contacts`);
        return directoryContacts;
      }
      
      return [];
    } catch (error) {
      console.error('Alternative directory search failed:', error);
      return [];
    }
  }

  /**
   * Search directory/organization contacts using People API (not Admin SDK)
   */
  async searchDirectoryContacts(query, options = {}) {
    try {
      console.log('Searching directory contacts...');
      
      // Use People API directory search endpoint (works for regular users)
      const params = new URLSearchParams();
      params.set('query', query);
      params.set('pageSize', options.pageSize || 50);
      
          // Include directory people fields
    const readMask = options.readMask || 
      'names,emailAddresses,phoneNumbers,photos,organizations';
      
      // Use readMask for searchDirectoryPeople (required parameter)
      params.set('readMask', readMask);
      
      // CRITICAL: sources[] parameter is REQUIRED for searchDirectoryPeople
      // According to Google People API docs, this specifies which directory sources to search
      params.set('sources', 'DIRECTORY_SOURCE_TYPE_DOMAIN_PROFILE');
      // Alternative sources: DIRECTORY_SOURCE_TYPE_DOMAIN_CONTACT
      
      const endpoint = `/people:searchDirectoryPeople?${params.toString()}`;
      
      console.log('Directory search endpoint:', endpoint);
      console.log('Full directory search URL:', `${this.baseURL}${endpoint}`);
      console.log('📋 IMPORTANT: This endpoint requires https://www.googleapis.com/auth/directory.readonly scope');
      console.log('📋 Current token only has contacts.readonly scope - this will likely cause a 400 or 403 error');
      
      const response = await this.makeRequest(endpoint, { method: 'GET' });
      
      console.log(`Found ${response.people?.length || 0} directory contacts`);
      
      // Transform directory people to contact format
      const directoryContacts = (response.people || []).map(person => ({
        id: person.resourceName,
        resourceName: person.resourceName,
        name: person.names?.[0]?.displayName || 'Unnamed Contact',
        email: person.emailAddresses?.[0]?.value || null,
        phone: person.phoneNumbers?.[0]?.value || null,
        photo: person.photos?.[0]?.url || null,
        organization: person.organizations?.[0]?.name || person.organizations?.[0]?.title || null,
        isDirectory: true,
        rawContact: person
      }));
      
      return directoryContacts;
    } catch (error) {
      console.error('Error searching directory contacts:', error);
      console.error('Directory search error details:', {
        status: error.status,
        message: error.message,
        response: error.response
      });
      
      // Try to get more details from the response
      if (error.response) {
        try {
          const errorText = await error.response.text();
          console.error('Directory search error response body:', errorText);
        } catch (e) {
          console.error('Could not read error response body');
        }
      }
      
      // If we don't have directory access, that's okay - just return empty
      if (error.status === 403) {
        console.warn('Directory access not available (403) - this usually means you don\'t have access to organization directory');
        return [];
      } else if (error.status === 400) {
        console.warn('Directory search failed (400) - the query might be invalid or directory search not supported for this account');
        return [];
      } else if (error.status === 404) {
        console.warn('Directory search endpoint not found (404) - this endpoint might not exist');
        return [];
      }
      
      // For other errors, still return empty to not break personal contacts
      console.warn('Directory search failed, continuing with personal contacts only');
      return [];
    }
  }

  /**
   * Search both personal and directory contacts (matching contacts.google.com scope)
   */
  async searchPersonalContacts(query, options = {}) {
    if (!query.trim()) {
      return {
        contacts: [],
        combined: [],
        errors: []
      };
    }

    const results = {
      contacts: [],
      directory: [],
      combined: [],
      errors: []
    };

    console.log(`Searching both personal and directory contacts for: "${query}"`);

    // Search personal contacts
    try {
      console.log('Searching personal contacts...');
      const contactsResponse = await this.searchContacts(query, options);
      results.contacts = contactsResponse.results || [];
      console.log(`Found ${results.contacts.length} personal contacts`);
    } catch (error) {
      console.error('Error searching personal contacts:', error);
      results.errors.push('Failed to search personal contacts: ' + error.message);
    }

    // Search directory contacts (organization users)
    try {
      console.log('Searching directory contacts...');
      let directoryContacts = await this.searchDirectoryContacts(query, options);
      
      // If primary method failed, try alternative
      if (directoryContacts.length === 0) {
        console.log('Primary directory search found nothing, trying alternative method...');
        directoryContacts = await this.searchDirectoryContactsAlternative(query, options);
      }
      
      results.directory = directoryContacts;
      console.log(`Found ${results.directory.length} directory contacts total`);
    } catch (error) {
      console.error('Error searching directory contacts:', error);
      results.errors.push('Failed to search directory contacts: ' + error.message);
    }

    // Transform personal contacts to consistent format
    const transformedPersonalContacts = results.contacts.map(contact => ({
      id: contact.person?.resourceName || contact.resourceName,
      resourceName: contact.person?.resourceName || contact.resourceName,
      name: contact.person?.names?.[0]?.displayName || 'Unnamed Contact',
      email: contact.person?.emailAddresses?.[0]?.value || null,
      phone: contact.person?.phoneNumbers?.[0]?.value || null,
      photo: contact.person?.photos?.[0]?.url || null,
      organization: contact.person?.organizations?.[0]?.name || null,
      isDirectory: false,
      rawContact: contact.person || contact
    }));

    // Combine personal and directory contacts (removing unused variable)
    // const allContacts = [...transformedPersonalContacts, ...results.directory];

    // Remove duplicates (prefer directory contacts over personal if same email)
    const uniqueContacts = [];
    const seenEmails = new Set();
    
    // Add directory contacts first (they're more authoritative)
    results.directory.forEach(contact => {
      if (contact.email && !seenEmails.has(contact.email)) {
        uniqueContacts.push(contact);
        seenEmails.add(contact.email);
      } else if (!contact.email) {
        uniqueContacts.push(contact);
      }
    });
    
    // Add personal contacts that don't duplicate directory contacts
    transformedPersonalContacts.forEach(contact => {
      if (contact.email && !seenEmails.has(contact.email)) {
        uniqueContacts.push(contact);
        seenEmails.add(contact.email);
      } else if (!contact.email) {
        uniqueContacts.push(contact);
      }
    });

    // Sort contacts by name
    results.combined = uniqueContacts.sort((a, b) => a.name.localeCompare(b.name));

    console.log(`Search complete: ${results.combined.length} total contacts found (${transformedPersonalContacts.length} personal + ${results.directory.length} directory)`);

    return results;
  }


  /**
   * Clear all cached data (for privacy)
   */
  async clearCache() {
    if (this.cache) {
      try {
        await this.cache.cleanup();
        console.log('Contacts cache cleared');
      } catch (error) {
        console.warn('Error clearing cache:', error);
      }
    }
  }

  /**
   * Get enhanced API analytics including token metrics
   */
  getAnalytics() {
    return { 
      ...this.analytics,
      tokenMetrics: this.tokenManager.getMetrics(),
      currentUserId: this.currentUserId
    };
  }

  /**
   * Get comprehensive status for debugging
   */
  async getStatus() {
    const status = {
      isInitialized: this.isInitialized,
      currentUserId: this.currentUserId,
      analytics: this.getAnalytics()
    };

    if (this.currentUserId) {
      try {
        status.tokenHealth = await this.tokenManager.getTokenHealth(this.currentUserId);
      } catch (error) {
        status.tokenHealthError = error.message;
      }
    }

    return status;
  }

  /**
   * Check what scopes the current token has
   */
  async checkTokenScopes(token) {
    try {
      const response = await fetch(`https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${token}`);
      const tokenInfo = await response.json();
      
      console.log('Full token info:', tokenInfo);
      
      if (tokenInfo.scope) {
        const scopes = tokenInfo.scope.split(' ');
        const hasContactsScope = scopes.some(scope => 
          scope.includes('contacts') || 
          scope === 'https://www.googleapis.com/auth/contacts' ||
          scope === 'https://www.googleapis.com/auth/contacts.readonly'
        );
        
        const hasDirectoryScope = scopes.some(scope => 
          scope.includes('directory') ||
          scope === 'https://www.googleapis.com/auth/admin.directory.user.readonly'
        );
        
        console.log('🔍 Token scopes analysis:', {
          allScopes: scopes,
          hasContactsScope,
          hasDirectoryScope,
          audience: tokenInfo.audience,
          issuedTo: tokenInfo.issued_to,
          expiresIn: tokenInfo.expires_in + ' seconds'
        });
      }
      
      return tokenInfo;
    } catch (error) {
      console.error('Error checking token scopes:', error);
      return null;
    }
  }

  /**
   * Check if user has granted contacts permission
   */
  async checkPermission() {
    try {
      console.log('Checking contacts permission...');
      
      // First check if we have a valid token
      const token = await this.getValidAccessToken();
      console.log('Token available:', !!token);
      
      // Check what scopes this token actually has
      if (token) {
        const tokenInfo = await this.checkTokenScopes(token);
        if (tokenInfo && tokenInfo.scope) {
          console.log('🔍 Token scopes available:', tokenInfo.scope);
          console.log('🔍 Looking for contacts scope:', [
            'https://www.googleapis.com/auth/contacts.readonly',
            'https://www.googleapis.com/auth/contacts'
          ]);
          
          const hasContactsScope = tokenInfo.scope.includes('https://www.googleapis.com/auth/contacts.readonly') || 
                                           tokenInfo.scope.includes('https://www.googleapis.com/auth/contacts');
          
          console.log('✅ Has contacts scope:', hasContactsScope);
          
          if (!hasContactsScope) {
            console.error('❌ Token does not have contacts scope. Available scopes:', tokenInfo.scope);
            console.log('💡 Solution: Clear cache and re-authenticate with contacts permission');
            return false;
          }
        } else {
          console.error('❌ Could not verify token scopes');
          return false;
        }
      }
      
      // Try to make a simple request to check permission
      console.log('Making test request to People API...');
      const response = await this.makeRequest('/people/me/connections?pageSize=1&personFields=names');
      console.log('Permission check successful:', response);
      return true;
    } catch (error) {
      console.error('Permission check failed:', error);
      
      if (error.message?.includes('No valid Google OAuth access token')) {
        return false;
      }
      
      if (error.status === 401 || error.status === 403) {
        return false;
      }
      
      // For other errors, still return false but log the error
      console.error('Unexpected error checking permission:', error);
      return false;
    }
  }

  /**
   * Clear token cache for reauthentication
   */
  clearTokenCache() {
    console.log('🧹 Clearing token cache for reauthentication');
    this.tokenManager.clearAllTokens();
  }
}

// Create and export singleton instance
const contactsAPI = new EnhancedGoogleContactsAPI();
export default contactsAPI;